 import { useState, useEffect } from "react";
 import { useNavigate } from "react-router-dom";
 import { useAuth } from "@/hooks/useAuth";
 import { supabase } from "@/integrations/supabase/client";
 import { format } from "date-fns";
 import { 
   Home, MapPin, FileText, Calendar, DollarSign, 
   Building2, LogOut, User, ChevronRight, Phone, Mail
 } from "lucide-react";
 
 interface LeadData {
   id: string;
   first_name: string | null;
   last_name: string | null;
   email: string | null;
   phone: string | null;
   property_address: string | null;
   city: string | null;
   zip_code: string | null;
   adu_type: string | null;
   adu_purpose: string[] | null;
   square_footage: number | null;
   finish_level: string | null;
   design_status: string | null;
   status: string | null;
   calendly_scheduled_at: string | null;
   created_at: string | null;
 }
 
 interface ProposalData {
   id: string;
   title: string | null;
   status: string | null;
   total_amount: number | null;
   created_at: string | null;
   valid_until: string | null;
 }
 
 interface ContractData {
   id: string;
   contract_number: string | null;
   status: string | null;
   total_amount: number | null;
   signed_at: string | null;
 }
 
 interface DocumentData {
   id: string;
   file_name: string;
   document_type: string;
   file_url: string;
   created_at: string | null;
 }
 
 const MyDashboard = () => {
   const { user, signOut, loading: authLoading } = useAuth();
   const navigate = useNavigate();
   const [leadData, setLeadData] = useState<LeadData | null>(null);
   const [proposals, setProposals] = useState<ProposalData[]>([]);
   const [contracts, setContracts] = useState<ContractData[]>([]);
   const [documents, setDocuments] = useState<DocumentData[]>([]);
   const [loading, setLoading] = useState(true);
 
   useEffect(() => {
     if (!authLoading && !user) {
       navigate("/login");
       return;
     }
 
     if (user) {
       // Redirect admin to admin dashboard
       if (user.email === "moein@udaadu.com") {
         navigate("/admin-dashboard");
         return;
       }
       fetchUserData();
     }
   }, [user, authLoading, navigate]);
 
   const fetchUserData = async () => {
     if (!user?.email) return;
 
     try {
       // Fetch lead data by email
       const { data: leadResult } = await supabase
         .from("crm_leads")
         .select("*")
         .eq("email", user.email)
         .maybeSingle();
 
       if (leadResult) {
         setLeadData(leadResult);
 
         // Fetch related proposals
         const { data: proposalsResult } = await supabase
           .from("crm_proposals")
           .select("*")
           .eq("lead_id", leadResult.id)
           .order("created_at", { ascending: false });
 
         if (proposalsResult) setProposals(proposalsResult);
 
         // Fetch related contracts
         const { data: contractsResult } = await supabase
           .from("crm_contracts")
           .select("*")
           .eq("lead_id", leadResult.id)
           .order("created_at", { ascending: false });
 
         if (contractsResult) setContracts(contractsResult);
 
         // Fetch related documents
         const { data: documentsResult } = await supabase
           .from("crm_documents")
           .select("*")
           .eq("lead_id", leadResult.id)
           .order("created_at", { ascending: false });
 
         if (documentsResult) setDocuments(documentsResult);
       }
     } catch (error) {
       console.error("Error fetching user data:", error);
     } finally {
       setLoading(false);
     }
   };
 
   const handleSignOut = async () => {
     await signOut();
     navigate("/login");
   };
 
   const formatCurrency = (amount: number | null) => {
     if (!amount) return "—";
     return new Intl.NumberFormat("en-US", {
       style: "currency",
       currency: "USD",
       maximumFractionDigits: 0,
     }).format(amount);
   };
 
   const getStatusColor = (status: string | null) => {
     switch (status?.toLowerCase()) {
       case "signed":
       case "accepted":
       case "completed":
         return "bg-green-500/20 text-green-400";
       case "pending":
       case "draft":
         return "bg-yellow-500/20 text-yellow-400";
       case "rejected":
       case "cancelled":
         return "bg-red-500/20 text-red-400";
       default:
         return "bg-gray-500/20 text-gray-400";
     }
   };
 
   // Estimate pricing based on square footage and finish level
   const getEstimatedPrice = () => {
     if (!leadData?.square_footage) return null;
     const sqft = leadData.square_footage;
     const baseRate = leadData.finish_level === "premium" ? 450 : 
                      leadData.finish_level === "standard" ? 350 : 300;
     return sqft * baseRate;
   };
 
   if (authLoading || loading) {
     return (
       <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
         <div className="text-center">
           <div className="w-12 h-12 border-4 border-[#E4683B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
           <p className="text-gray-400">Loading your dashboard...</p>
         </div>
       </div>
     );
   }
 
   return (
     <div className="min-h-screen bg-[#0a0a0a]">
       {/* Header */}
       <header 
         className="sticky top-0 z-50 px-6 py-4"
         style={{
           backgroundColor: "#0a0a0a",
           boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
         }}
       >
         <div className="max-w-7xl mx-auto flex items-center justify-between">
           <div className="flex items-center gap-3">
             <div 
               className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{
                 backgroundColor: "#141414",
                 boxShadow: "4px 4px 8px #000000, -4px -4px 8px #1a1a1a",
               }}
             >
               <Home className="w-5 h-5 text-[#E4683B]" />
             </div>
             <div>
               <h1 className="text-xl font-bold text-white">My ADU Dashboard</h1>
               <p className="text-sm text-gray-400">UDA ADU</p>
             </div>
           </div>
           
           <div className="flex items-center gap-4">
             <div className="text-right hidden sm:block">
               <p className="text-sm text-gray-400">Welcome back,</p>
               <p className="text-white font-medium">
                 {leadData?.first_name || user?.email?.split("@")[0]}
               </p>
             </div>
             <button
               onClick={handleSignOut}
               className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-400 hover:text-white transition-colors"
               style={{
                 backgroundColor: "#141414",
                 boxShadow: "4px 4px 8px #000000, -4px -4px 8px #1a1a1a",
               }}
             >
               <LogOut className="w-4 h-4" />
               <span className="hidden sm:inline">Sign Out</span>
             </button>
           </div>
         </div>
       </header>
 
       <main className="max-w-7xl mx-auto px-6 py-8">
         {!leadData ? (
           <div 
             className="text-center py-16 rounded-2xl"
             style={{
               backgroundColor: "#0a0a0a",
               boxShadow: "inset 4px 4px 8px #000000, inset -4px -4px 8px #141414",
             }}
           >
             <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
             <h2 className="text-xl font-semibold text-white mb-2">No Project Found</h2>
             <p className="text-gray-400 mb-6">
               Start your ADU journey by getting a free quote!
             </p>
             <button
               onClick={() => navigate("/what-fits-my-lot")}
               className="px-6 py-3 rounded-xl font-semibold text-white transition-all"
               style={{
                 backgroundColor: "#E4683B",
                 boxShadow: "4px 4px 8px #000000, -4px -4px 8px #141414",
               }}
             >
               Get Started
             </button>
           </div>
         ) : (
           <div className="space-y-8">
             {/* Property Overview Card */}
             <div 
               className="p-6 rounded-2xl"
               style={{
                 backgroundColor: "#0a0a0a",
                 boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
               }}
             >
               <div className="flex items-center gap-3 mb-6">
                 <div 
                   className="w-12 h-12 rounded-xl flex items-center justify-center"
                   style={{
                     backgroundColor: "#141414",
                     boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                   }}
                 >
                   <MapPin className="w-6 h-6 text-[#E4683B]" />
                 </div>
                 <div>
                   <h2 className="text-xl font-bold text-white">Property Details</h2>
                   <p className="text-gray-400 text-sm">Your ADU project information</p>
                 </div>
               </div>
 
               <div className="grid md:grid-cols-2 gap-6">
                 <div className="space-y-4">
                   <div>
                     <p className="text-gray-500 text-sm mb-1">Address</p>
                     <p className="text-white font-medium">
                       {leadData.property_address || "Not specified"}
                     </p>
                     {leadData.city && (
                       <p className="text-gray-400 text-sm">
                         {leadData.city}{leadData.zip_code ? `, ${leadData.zip_code}` : ""}
                       </p>
                     )}
                   </div>
                   
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <p className="text-gray-500 text-sm mb-1">ADU Type</p>
                       <p className="text-white capitalize">
                         {leadData.adu_type?.replace(/_/g, " ") || "—"}
                       </p>
                     </div>
                     <div>
                       <p className="text-gray-500 text-sm mb-1">Size</p>
                       <p className="text-white">
                         {leadData.square_footage ? `${leadData.square_footage} sq ft` : "—"}
                       </p>
                     </div>
                   </div>
 
                   <div>
                     <p className="text-gray-500 text-sm mb-1">Purpose</p>
                     <div className="flex flex-wrap gap-2">
                       {leadData.adu_purpose?.map((purpose, i) => (
                         <span 
                           key={i}
                           className="px-3 py-1 rounded-full text-sm bg-[#E4683B]/20 text-[#E4683B]"
                         >
                           {purpose.replace(/_/g, " ")}
                         </span>
                       )) || <span className="text-gray-400">—</span>}
                     </div>
                   </div>
                 </div>
 
                 <div className="space-y-4">
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <p className="text-gray-500 text-sm mb-1">Finish Level</p>
                       <p className="text-white capitalize">{leadData.finish_level || "—"}</p>
                     </div>
                     <div>
                       <p className="text-gray-500 text-sm mb-1">Design Status</p>
                       <p className="text-white capitalize">
                         {leadData.design_status?.replace(/_/g, " ") || "—"}
                       </p>
                     </div>
                   </div>
 
                   <div>
                     <p className="text-gray-500 text-sm mb-1">Project Status</p>
                     <span className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(leadData.status)}`}>
                       {leadData.status?.replace(/_/g, " ") || "New"}
                     </span>
                   </div>
 
                   <div>
                     <p className="text-gray-500 text-sm mb-1">Submitted On</p>
                     <p className="text-white">
                       {leadData.created_at 
                         ? format(new Date(leadData.created_at), "MMMM d, yyyy")
                         : "—"}
                     </p>
                   </div>
                 </div>
               </div>
             </div>
 
             {/* Estimate & Appointment Cards */}
             <div className="grid md:grid-cols-2 gap-6">
               {/* Price Estimate Card */}
               <div 
                 className="p-6 rounded-2xl"
                 style={{
                   backgroundColor: "#0a0a0a",
                   boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
                 }}
               >
                 <div className="flex items-center gap-3 mb-4">
                   <div 
                     className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{
                       backgroundColor: "#141414",
                       boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                     }}
                   >
                     <DollarSign className="w-5 h-5 text-green-400" />
                   </div>
                   <h3 className="text-lg font-semibold text-white">Estimated Cost</h3>
                 </div>
                 
                 <div className="text-center py-4">
                   <p className="text-4xl font-bold text-[#E4683B]">
                     {getEstimatedPrice() 
                       ? formatCurrency(getEstimatedPrice())
                       : "Contact for Quote"}
                   </p>
                   {leadData.square_footage && (
                     <p className="text-gray-400 text-sm mt-2">
                       Based on {leadData.square_footage} sq ft • {leadData.finish_level || "Standard"} finish
                     </p>
                   )}
                 </div>
                 
                 <p className="text-gray-500 text-xs text-center mt-4">
                   * Estimate based on typical Bay Area ADU construction costs
                 </p>
               </div>
 
               {/* Appointment Card */}
               <div 
                 className="p-6 rounded-2xl"
                 style={{
                   backgroundColor: "#0a0a0a",
                   boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
                 }}
               >
                 <div className="flex items-center gap-3 mb-4">
                   <div 
                     className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{
                       backgroundColor: "#141414",
                       boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                     }}
                   >
                     <Calendar className="w-5 h-5 text-blue-400" />
                   </div>
                   <h3 className="text-lg font-semibold text-white">Consultation</h3>
                 </div>
                 
                 {leadData.calendly_scheduled_at ? (
                   <div className="text-center py-4">
                     <p className="text-gray-400 text-sm mb-2">Your appointment is scheduled for</p>
                     <p className="text-xl font-semibold text-white">
                       {format(new Date(leadData.calendly_scheduled_at), "EEEE, MMMM d")}
                     </p>
                     <p className="text-[#E4683B]">
                       {format(new Date(leadData.calendly_scheduled_at), "h:mm a")}
                     </p>
                   </div>
                 ) : (
                   <div className="text-center py-4">
                     <p className="text-gray-400 mb-4">Schedule a free consultation with our team</p>
                     <button
                       onClick={() => window.open("https://calendly.com/uda-adu/consultation", "_blank")}
                       className="px-6 py-3 rounded-xl font-semibold text-[#1a1a1a] transition-all"
                       style={{
                         backgroundColor: "#f5f5f5",
                         boxShadow: "8px 8px 16px #000000, -8px -8px 16px #1a1a1a",
                       }}
                     >
                       Schedule Now
                     </button>
                   </div>
                 )}
               </div>
             </div>
 
             {/* Proposals Section */}
             {proposals.length > 0 && (
               <div 
                 className="p-6 rounded-2xl"
                 style={{
                   backgroundColor: "#0a0a0a",
                   boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
                 }}
               >
                 <div className="flex items-center gap-3 mb-6">
                   <div 
                     className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{
                       backgroundColor: "#141414",
                       boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                     }}
                   >
                     <FileText className="w-5 h-5 text-purple-400" />
                   </div>
                   <h3 className="text-lg font-semibold text-white">Proposals</h3>
                 </div>
 
                 <div className="space-y-3">
                   {proposals.map((proposal) => (
                     <div 
                       key={proposal.id}
                       className="flex items-center justify-between p-4 rounded-xl"
                       style={{
                         backgroundColor: "#141414",
                         boxShadow: "4px 4px 8px #000000, -4px -4px 8px #1a1a1a",
                       }}
                     >
                       <div>
                         <p className="text-white font-medium">{proposal.title || "ADU Proposal"}</p>
                         <p className="text-gray-400 text-sm">
                           {proposal.created_at && format(new Date(proposal.created_at), "MMM d, yyyy")}
                         </p>
                       </div>
                       <div className="flex items-center gap-4">
                         <span className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(proposal.status)}`}>
                           {proposal.status}
                         </span>
                         <p className="text-[#E4683B] font-semibold">
                           {formatCurrency(proposal.total_amount)}
                         </p>
                         <ChevronRight className="w-5 h-5 text-gray-500" />
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
 
             {/* Contracts Section */}
             {contracts.length > 0 && (
               <div 
                 className="p-6 rounded-2xl"
                 style={{
                   backgroundColor: "#0a0a0a",
                   boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
                 }}
               >
                 <div className="flex items-center gap-3 mb-6">
                   <div 
                     className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{
                       backgroundColor: "#141414",
                       boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                     }}
                   >
                     <FileText className="w-5 h-5 text-green-400" />
                   </div>
                   <h3 className="text-lg font-semibold text-white">Contracts</h3>
                 </div>
 
                 <div className="space-y-3">
                   {contracts.map((contract) => (
                     <div 
                       key={contract.id}
                       className="flex items-center justify-between p-4 rounded-xl"
                       style={{
                         backgroundColor: "#141414",
                         boxShadow: "4px 4px 8px #000000, -4px -4px 8px #1a1a1a",
                       }}
                     >
                       <div>
                         <p className="text-white font-medium">
                           Contract #{contract.contract_number || "—"}
                         </p>
                         {contract.signed_at && (
                           <p className="text-gray-400 text-sm">
                             Signed {format(new Date(contract.signed_at), "MMM d, yyyy")}
                           </p>
                         )}
                       </div>
                       <div className="flex items-center gap-4">
                         <span className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(contract.status)}`}>
                           {contract.status}
                         </span>
                         <p className="text-[#E4683B] font-semibold">
                           {formatCurrency(contract.total_amount)}
                         </p>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
 
             {/* Documents Section */}
             {documents.length > 0 && (
               <div 
                 className="p-6 rounded-2xl"
                 style={{
                   backgroundColor: "#0a0a0a",
                   boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
                 }}
               >
                 <div className="flex items-center gap-3 mb-6">
                   <div 
                     className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{
                       backgroundColor: "#141414",
                       boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                     }}
                   >
                     <FileText className="w-5 h-5 text-blue-400" />
                   </div>
                   <h3 className="text-lg font-semibold text-white">Documents</h3>
                 </div>
 
                 <div className="grid sm:grid-cols-2 gap-3">
                   {documents.map((doc) => (
                     <a
                       key={doc.id}
                       href={doc.file_url}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="flex items-center gap-3 p-4 rounded-xl hover:opacity-80 transition-opacity"
                       style={{
                         backgroundColor: "#141414",
                         boxShadow: "4px 4px 8px #000000, -4px -4px 8px #1a1a1a",
                       }}
                     >
                       <FileText className="w-8 h-8 text-[#E4683B]" />
                       <div className="flex-1 min-w-0">
                         <p className="text-white font-medium truncate">{doc.file_name}</p>
                         <p className="text-gray-400 text-sm capitalize">
                           {doc.document_type.replace(/_/g, " ")}
                         </p>
                       </div>
                       <ChevronRight className="w-5 h-5 text-gray-500" />
                     </a>
                   ))}
                 </div>
               </div>
             )}
 
             {/* Contact Card */}
             <div 
               className="p-6 rounded-2xl"
               style={{
                 backgroundColor: "#0a0a0a",
                 boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
               }}
             >
               <div className="flex items-center gap-3 mb-4">
                 <div 
                   className="w-10 h-10 rounded-xl flex items-center justify-center"
                   style={{
                     backgroundColor: "#141414",
                     boxShadow: "inset 2px 2px 4px #000000, inset -2px -2px 4px #1a1a1a",
                   }}
                 >
                   <User className="w-5 h-5 text-[#E4683B]" />
                 </div>
                 <h3 className="text-lg font-semibold text-white">Need Help?</h3>
               </div>
               
               <div className="flex flex-wrap gap-4">
                 <a
                   href="tel:+14085551234"
                   className="flex items-center gap-2 px-4 py-2 rounded-xl text-white transition-all hover:opacity-80"
                   style={{
                     backgroundColor: "#f5f5f5",
                     color: "#1a1a1a",
                     boxShadow: "8px 8px 16px #000000, -8px -8px 16px #1a1a1a",
                   }}
                 >
                   <Phone className="w-4 h-4" />
                   Call Us
                 </a>
                 <a
                   href="mailto:info@udaadu.com"
                   className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                   style={{
                     backgroundColor: "#f5f5f5",
                     color: "#1a1a1a",
                     boxShadow: "8px 8px 16px #000000, -8px -8px 16px #1a1a1a",
                   }}
                 >
                   <Mail className="w-4 h-4" />
                   Email Us
                 </a>
               </div>
             </div>
           </div>
         )}
       </main>
     </div>
   );
 };
 
 export default MyDashboard;